import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { LEAD_STATUSES } from "../../data/content";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { Download, Trash2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";

const STATUS_COLORS = {
  NEW: "bg-flame/15 text-flame border-flame/30",
  PAID: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  HANDED_OVER: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  LOST: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const q = status === "ALL" ? "" : `?status=${status}`;
    api.get(`/admin/leads${q}`).then((res) => setLeads(res.data)).catch(() => toast.error("Could not load leads"));
  };
  useEffect(load, [status]);

  const update = async (leadId, payload) => {
    await api.patch(`/admin/leads/${leadId}`, payload).catch(() => toast.error("Update failed"));
    load();
    if (active?.lead_id === leadId) {
      const res = await api.get("/admin/leads");
      setActive(res.data.find((l) => l.lead_id === leadId) || active);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/leads/${active.lead_id}/notes`, { text: note }).catch(() => toast.error("Note failed"));
    setNote("");
    const res = await api.get("/admin/leads");
    const fresh = res.data.find((l) => l.lead_id === active.lead_id);
    if (fresh) setActive(fresh);
    load();
  };

  const remove = async (leadId) => {
    await api.delete(`/admin/leads/${leadId}`).catch(() => toast.error("Delete failed"));
    setActive(null);
    load();
    toast.success("Lead removed");
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-white">Leads CRM</h1>
          <p className="mt-2 text-sm text-slate-400">{leads.length} lead{leads.length === 1 ? "" : "s"}</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="leads-status-filter" className="w-56 bg-navy-800 border-white/10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-navy-800 border-white/10">
            <SelectItem value="ALL">ALL STATUSES</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-8 rounded-xl border border-white/10 bg-navy-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Name</TableHead>
              <TableHead className="text-slate-400">Company</TableHead>
              <TableHead className="text-slate-400 hidden md:table-cell">Application</TableHead>
              <TableHead className="text-slate-400 hidden lg:table-cell">Delivery</TableHead>
              <TableHead className="text-slate-400 hidden lg:table-cell">Budget</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400 hidden sm:table-cell">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} onClick={() => setActive(l)} className="border-white/5 cursor-pointer hover:bg-white/5">
                <TableCell className="font-semibold text-white">
                  {l.full_name}
                  {l.is_demo && <Badge className="ml-2 bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[8px]">DEMO</Badge>}
                </TableCell>
                <TableCell className="text-slate-300">{l.company || "—"}</TableCell>
                <TableCell className="text-slate-400 hidden md:table-cell">{l.application_type || "—"}</TableCell>
                <TableCell className="text-slate-400 hidden lg:table-cell">{l.delivery_preference || "—"}</TableCell>
                <TableCell className="text-slate-400 hidden lg:table-cell">{l.estimated_budget || "—"}</TableCell>
                <TableCell>
                  <Badge className={`font-mono text-[9px] ${STATUS_COLORS[l.status] || "bg-white/10 text-slate-300 border-white/10"}`}>
                    {l.status.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-500 text-xs hidden sm:table-cell">{l.created_at?.slice(0, 10)}</TableCell>
              </TableRow>
            ))}
            {!leads.length && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">No leads in this view.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-navy-800 border-white/10 max-w-3xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
          {active && (
            <div data-testid="lead-detail-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white flex items-center gap-3">
                  {active.full_name}
                  {active.is_demo && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 font-mono text-[9px]">DEMO</Badge>}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-mono">
                {[["EMAIL", active.email], ["PHONE", active.phone || "—"], ["COMPANY", active.company || "—"],
                  ["LOCATION", [active.city, active.country].filter(Boolean).join(", ") || "—"], ["SIZE", active.company_size || "—"],
                  ["TYPE", active.application_type || "—"], ["DELIVERY", active.delivery_preference || "—"], ["MODE", active.mode_preference || "—"],
                  ["TIMELINE", active.expected_timeline || "—"], ["BUDGET", active.estimated_budget || "—"], ["SOURCE", active.lead_source || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-navy-900/70 p-3">
                    <p className="text-[9px] tracking-widest text-slate-500">{k}</p>
                    <p className="mt-1 text-slate-200 break-words">{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-4 text-sm">
                {[["BUSINESS PROBLEM", active.business_problem], ["PROJECT DESCRIPTION", active.project_description],
                  ["TARGET USERS", active.target_users], ["KEY FEATURES", active.key_features], ["INTEGRATIONS", active.integrations],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}>
                    <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-1">{k}</p>
                    <p className="text-slate-300 leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>

              {active.files?.length > 0 && (
                <div className="mt-5">
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">ATTACHMENTS</p>
                  <div className="flex flex-wrap gap-2">
                    {active.files.map((f) => (
                      <a key={f.file_id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}`} target="_blank" rel="noreferrer"
                        data-testid={`lead-file-${f.file_id}`}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3.5 py-1.5 text-[11px] text-slate-300 hover:border-flame hover:text-flame transition-colors">
                        <Download className="w-3 h-3" /> {f.original_filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">STATUS</p>
                  <Select value={active.status} onValueChange={(v) => update(active.lead_id, { status: v })}>
                    <SelectTrigger data-testid="lead-status-select" className="bg-navy-900/60 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-navy-800 border-white/10">
                      {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">ASSIGNED TO</p>
                  <Input defaultValue={active.assigned_to} data-testid="lead-assigned-input" onBlur={(e) => update(active.lead_id, { assigned_to: e.target.value })} className="bg-navy-900/60 border-white/10" />
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">NEXT FOLLOW-UP</p>
                  <Input type="date" defaultValue={active.next_follow_up} data-testid="lead-followup-input" onBlur={(e) => update(active.lead_id, { next_follow_up: e.target.value })} className="bg-navy-900/60 border-white/10" />
                </div>
              </div>

              <div className="mt-6">
                <p className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">NOTES</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(active.notes || []).map((n, i) => (
                    <div key={i} className="rounded-lg bg-navy-900/70 p-3 text-xs">
                      <p className="text-slate-300">{n.text}</p>
                      <p className="mt-1 font-mono text-[9px] text-slate-600">{n.by} · {n.at?.slice(0, 16).replace("T", " ")}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="lead-note-input" className="bg-navy-900/60 border-white/10" />
                  <Button onClick={addNote} data-testid="lead-add-note-btn" className="bg-flame hover:bg-flame-hover text-white shrink-0">ADD</Button>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button variant="outline" onClick={() => remove(active.lead_id)} data-testid="lead-delete-btn" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete lead
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
